import { Button, Modal, ModalBody, ModalContent, ModalHeader } from '@nextui-org/react';
import React, { useEffect } from 'react';
import { IconClose, IconCreditFlashOrange } from '@/assets';
import { Images } from '@/components';
import AvatarPlaceholder from '@/components/avatar-default';
import { useAuth } from '@/provider/UserProvider';
import AvatarDecor from '@/components/avatar-decor';
import moment from 'moment';
import { AvatarDecor as AvatarDecorProps } from './useFetch';
import { formatCurrency } from '@/utils';
import useBuy from './useBuy';
import Link from 'next/link';

const PreviewDecor: React.FC<{
  isOpen: boolean;
  onOpenChange: () => void;
  info?: AvatarDecorProps,
  isBoughtDecor: (id: number) => boolean,
  refresh: () => void,
}> = ({ isOpen, onOpenChange, info, refresh, isBoughtDecor }) => {
  const { auth } = useAuth();
  const { handleBuy, loading, result, setResult } = useBuy();

  useEffect(() => {
    setResult(null)
  }, [info]);

  if (!info) return null;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      scrollBehavior="inside"
      placement="center"
    >
      <ModalContent className="w-[650px] max-w-full rounded-[12px]">
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center justify-between p-[20px]">
              <span className="text-[16px] font-[600]">Profile Decoration</span>
              <div
                onClick={onClose}
                className="flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[8px] bg-gray-100"
              >
                <IconClose />
              </div>
            </ModalHeader>
            <ModalBody className="max-h-[400px] px-[20px]">
              <div className="flex sm:flex-col-reverse">
                <div className="w-[40%] sm:w-full pr-[20px] sm:pr-0">
                  <div className="flex justify-center">
                    <div className="max-w-[100px]">
                      <Images alt="image" className="h-auto" src={info.decoUrl} />
                    </div>
                  </div>
                  <p aria-checked={Boolean(result)} className="text-[14px] font-semibold mt-3 mb-5 aria-checked:mb-2 line-clamp-1 capitalize">{info.name}</p>
                  {result && (
                    <p aria-checked={result.success} className="text-[12px] text-red-500 aria-checked:text-green-500 mb-2">
                      {result.message} {result.success && <Link href="/update-profile" className="text-black underline font-medium">Setup now</Link>}
                    </p>
                  )}
                  <Button 
                    isDisabled={isBoughtDecor(info.id)}
                    isLoading={loading} 
                    onClick={() => !isBoughtDecor(info.id) && handleBuy(info.id, info.price, refresh)} 
                    className="w-full mb-3 h-[36px] rounded-full bg-primary text-white"
                  >
                    <div className="flex justify-between items-center w-full">
                      <span className="font-medium">Purchase</span>
                      <div className="bg-white text-orange-500 font-medium pr-2 pl-[1px] rounded-full text-[12px] flex items-center">
                        <IconCreditFlashOrange className="scale-80" />
                        <span>{formatCurrency(info.price || 0)}</span>
                      </div>
                    </div>
                  </Button>
                </div>
                <div className="w-[60%] sm:w-full pl-[20px] sm:pl-0 sm:mb-6">
                  <p className="text-center font-semibold mb-2 text-[14px]">Preview</p>
                  <div className="border-1 border-gray-200 rounded-lg overflow-hidden w-full">
                    <div className="h-[50px] bg-gray-50"></div>
                    <div className="px-5 border-t-1 border-gray-200 pb-3 flex">
                      <div className="w-[60px] h-[60px] rounded-full relative -mt-5">
                        <AvatarPlaceholder
                          address={auth.user.publicAddress}
                          src={auth.user.picture}
                          className="rounded-full object-cover w-full h-full"
                        />
                        <AvatarDecor scale={34} url={info.decoUrl} />
                      </div>
                      <div className="pl-[10px] pt-1 flex flex-col gap-[1px]">
                        <p className="text-[13px] font-medium line-clamp-1 leading-none">{auth.user.username ?? 'Unnamed'}</p>
                        <span className="text-[12px] text-gray-500">Joined {moment(Date.now()).format('ll')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default PreviewDecor;
